// ---------------------------------------------------------------------------
// usePersistedFlag (AL-1850)
// ---------------------------------------------------------------------------
// A boolean that survives reloads. Reads and writes are wrapped because
// storage throws in private windows and when site data is blocked, and a
// sidebar that cannot remember its width is still a usable sidebar.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';

function read(key: string, fallback: boolean): boolean {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    return stored === 'true';
  } catch {
    return fallback;
  }
}

function write(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Storage unavailable — the preference is lost on reload, nothing else.
  }
}

export function usePersistedFlag(
  key: string,
  fallback: boolean
): readonly [boolean, (next: boolean) => void, () => void] {
  const [value, setValue] = useState<boolean>(() => read(key, fallback));

  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      write(key, next);
    },
    [key]
  );

  const toggle = useCallback(() => {
    setValue(prev => {
      const next = !prev;
      write(key, next);
      return next;
    });
  }, [key]);

  return [value, set, toggle] as const;
}
